import React, { useState } from 'react';
import { DATE_FORMATS, formatDateByFormat } from '../../../../../../../../utils/dateUtils';
import { useAskAicaSurface } from '../../context/AskAicaSurfaceContext';
import { leftPanelCloseIcon, rightPanelCloseIcon } from '../../../../../../../../assets/hostedassets';
import styles from './PortfolioConversationSidebar.module.scss';

/** Left sidebar for the org-less portfolio surface: user-scoped conversation
 * history (GET /chat/conversations/user — never the org-scoped list). Restored
 * 2026-08-10 as a TRIMMED, leads-style sidebar for visual parity — "Your
 * Conversations" only (0 or 1 row under the single-conversation model), no
 * "Team's Conversations" section and no "New Chat" button (single-conversation
 * model: resume-on-open loads the one active conversation; Delete Chat + the
 * next message starts the next one). emptyHint mirrors the leads
 * ConversationSidebar's "No conversations yet" verbatim. 401 (no
 * platformUserId principal) is handled upstream by useGlobalAskAica, which
 * resolves to an empty `conversations` list here — this component just renders
 * that as the same empty state as "no history yet". Reads/writes go through
 * the shared surface context, not props — mirrors the leads ConversationSidebar's
 * own direct-slice pattern, but for the portfolio provider instead. */
const PortfolioConversationSidebar: React.FC = () => {
    const { conversations, isLoadingConversations, selectedConversationId, loadConversation, isLoading } = useAskAicaSurface();
    const [collapsed, setCollapsed] = useState(false);

    if (collapsed) {
        return (
            <div className={styles.SidebarCollapsed}>
                <button className={styles.CollapseBtn} onClick={() => setCollapsed(false)} title="Expand conversations">
                    <img src={rightPanelCloseIcon} alt="" width="18" height="18" />
                </button>
            </div>
        );
    }

    return (
        <div className={styles.Sidebar} data-testid="portfolio-conversation-sidebar">
            <div className={styles.TopRow}>
                <span />
                <button className={styles.CollapseBtn} onClick={() => setCollapsed(true)} title="Collapse conversations">
                    <img src={leftPanelCloseIcon} alt="" width="18" height="18" />
                </button>
            </div>
            {isLoadingConversations ? (
                <div className={styles.Shimmer}>
                    {[1, 2, 3].map((index) => (
                        <div key={index} className={styles.ShimmerRow} />
                    ))}
                </div>
            ) : (
                <div className={styles.Section}>
                    <span className={styles.SectionHeader}>Your Conversations</span>
                    {conversations.length === 0 ? (
                        <span className={styles.EmptyHint}>No conversations yet</span>
                    ) : (
                        conversations.map((conversation) => (
                            <button
                                key={conversation.conversation_id}
                                type="button"
                                className={`${styles.Row} ${
                                    selectedConversationId === conversation.conversation_id ? styles.RowSelected : ''
                                }`}
                                onClick={() => loadConversation?.(conversation.conversation_id)}
                                disabled={isLoading}
                            >
                                <span className={styles.RowTitle}>{conversation.title || 'Untitled'}</span>
                                {conversation.last_message_at && (
                                    <span className={styles.RowSubtitle}>
                                        {formatDateByFormat(conversation.last_message_at, DATE_FORMATS.LIBRARY_FILE_TIMESTAMP)}
                                    </span>
                                )}
                            </button>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default PortfolioConversationSidebar;
